// src/hooks/useGBP.js - Hook for accessing GBP data from GBPStore
import { useMemo } from 'react';
import useGBPStore from '../stores/GBPStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing Google Business Profile data and operations
 *
 * Uses Zustand GBPStore with individual selectors for optimal React re-rendering.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} GBP data, operations, and computed values
 */
export const useGBP = () => {
  const socket = useSocket();

  // 🚨 Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change

  // Connection status
  const gbpConnection = useGBPStore(state => state.gbpConnection);
  const gbpConnectionLoading = useGBPStore(state => state.gbpConnectionLoading);

  // OAuth flow
  const oauthUrl = useGBPStore(state => state.oauthUrl);
  const oauthInitiating = useGBPStore(state => state.oauthInitiating);

  // Accounts & Locations
  const accounts = useGBPStore(state => state.accounts);
  const accountsLoading = useGBPStore(state => state.accountsLoading);

  const locations = useGBPStore(state => state.locations);
  const locationsLoading = useGBPStore(state => state.locationsLoading);

  const selectedLocations = useGBPStore(state => state.selectedLocations);
  const savingLocations = useGBPStore(state => state.savingLocations);

  // Reviews
  const reviews = useGBPStore(state => state.reviews);
  const reviewsLoading = useGBPStore(state => state.reviewsLoading);
  const reviewsPagination = useGBPStore(state => state.reviewsPagination);

  // Review operations
  const replyingToReview = useGBPStore(state => state.replyingToReview);
  const deletingReplyForReview = useGBPStore(state => state.deletingReplyForReview);

  // Review request link
  const reviewLink = useGBPStore(state => state.reviewLink);
  const reviewLinkLoading = useGBPStore(state => state.reviewLinkLoading);

  // Disconnection
  const disconnecting = useGBPStore(state => state.disconnecting);

  // Connection state
  const isConnected = useGBPStore(state => state.isConnected);

  // ================================
  // OPERATIONS (functions don't change, so we can get them once)
  // ================================

  const loadGBPConnectionStatus = useGBPStore(state => state.loadGBPConnectionStatus);
  const startGBPOAuth = useGBPStore(state => state.startGBPOAuth);
  const fetchGBPAccounts = useGBPStore(state => state.fetchGBPAccounts);
  const fetchGBPLocations = useGBPStore(state => state.fetchGBPLocations);
  const saveGBPLocations = useGBPStore(state => state.saveGBPLocations);
  const fetchGBPReviews = useGBPStore(state => state.fetchGBPReviews);
  const replyToGBPReview = useGBPStore(state => state.replyToGBPReview);
  const deleteGBPReviewReply = useGBPStore(state => state.deleteGBPReviewReply);
  const getGBPReviewLink = useGBPStore(state => state.getGBPReviewLink);
  const disconnectGBP = useGBPStore(state => state.disconnectGBP);

  // Utility functions
  const getConnectionStatus = useGBPStore(state => state.getConnectionStatus);
  const isGBPConnected = useGBPStore(state => state.isGBPConnected);
  const isTokenExpired = useGBPStore(state => state.isTokenExpired);
  const getStatusMessage = useGBPStore(state => state.getStatusMessage);
  const setSelectedLocations = useGBPStore(state => state.setSelectedLocations);

  // Internal initialization state (for fallback check)
  const _initialized = useGBPStore(state => state._initialized);

  // ================================
  // COMPUTED VALUES (memoized)
  // ================================

  /**
   * Current connection status
   */
  const currentStatus = useMemo(() => {
    return getConnectionStatus();
  }, [getConnectionStatus]);

  /**
   * Status message for UI display
   */
  const statusMessage = useMemo(() => {
    return getStatusMessage();
  }, [getStatusMessage]);

  /**
   * Whether user is currently connected
   */
  const connected = useMemo(() => {
    return isGBPConnected();
  }, [isGBPConnected]);

  /**
   * Whether token has expired
   */
  const tokenExpired = useMemo(() => {
    return isTokenExpired();
  }, [isTokenExpired]);

  /**
   * Get connected locations from connection object
   */
  const connectedLocations = useMemo(() => {
    return gbpConnection?.connected_locations || [];
  }, [gbpConnection]);

  /**
   * Get primary (first) connected location
   */
  const primaryLocation = useMemo(() => {
    const locs = gbpConnection?.connected_locations || [];
    return locs.length > 0 ? locs[0] : null;
  }, [gbpConnection]);

  /**
   * Get account name (Google email)
   */
  const accountName = useMemo(() => {
    return gbpConnection?.account_name || null;
  }, [gbpConnection]);

  /**
   * Check if setup requires action (for OAuth flow)
   */
  const requiresSetup = useMemo(() => {
    return ['disconnected', 'token_expired'].includes(currentStatus);
  }, [currentStatus]);

  /**
   * Check if locations need to be selected
   * (OAuth succeeded but no locations saved yet)
   */
  const needsLocationSelection = useMemo(() => {
    return connected && connectedLocations.length === 0;
  }, [connected, connectedLocations]);

  /**
   * Get review stats from primary location
   */
  const reviewStats = useMemo(() => {
    if (!primaryLocation?.reviewStats) {
      return {
        averageRating: 0,
        totalReviewCount: 0
      };
    }
    return primaryLocation.reviewStats;
  }, [primaryLocation]);

  /**
   * Count pending replies (reviews without replies)
   */
  const pendingRepliesCount = useMemo(() => {
    return reviews.filter(review => !review.reviewReply).length;
  }, [reviews]);

  /**
   * Check if any operation is loading
   */
  const anyLoading = useMemo(() => {
    return gbpConnectionLoading ||
           oauthInitiating ||
           accountsLoading ||
           locationsLoading ||
           savingLocations ||
           reviewsLoading ||
           disconnecting;
  }, [gbpConnectionLoading, oauthInitiating, accountsLoading, locationsLoading, savingLocations, reviewsLoading, disconnecting]);

  // ================================
  // FALLBACK INITIALIZATION
  // ================================

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useGBP: Store not initialized at app level, trying fallback');
    socket.initializeStore('GBPStore');
  }

  // ================================
  // RETURN INTERFACE
  // ================================

  return {
    // Connection status
    gbpConnection,
    gbpConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Accounts & Locations
    accounts,
    accountsLoading,
    locations,
    locationsLoading,
    selectedLocations,
    savingLocations,
    connectedLocations,
    primaryLocation,
    accountName,
    needsLocationSelection,

    // Reviews
    reviews,
    reviewsLoading,
    reviewsPagination,
    reviewStats,
    pendingRepliesCount,

    // Review operations
    replyingToReview,
    deletingReplyForReview,

    // Review request link
    reviewLink,
    reviewLinkLoading,

    // Disconnection
    disconnecting,

    // Computed values
    anyLoading,

    // Operations
    loadGBPConnectionStatus,
    startGBPOAuth,
    fetchGBPAccounts,
    fetchGBPLocations,
    saveGBPLocations,
    setSelectedLocations,
    fetchGBPReviews,
    replyToGBPReview,
    deleteGBPReviewReply,
    getGBPReviewLink,
    disconnectGBP,

    // Legacy/convenience aliases
    loading: gbpConnectionLoading
  };
};

export default useGBP;
